import type { RfqSubmission } from '../types';
import { timberQuoteStreamKeyFromParts } from './timberQuoteStreamKey';

/**
 * Latest quote plus its superseded predecessors for one quote stream
 * (species + structure set on timber RFQs, structure set on CNC quotes).
 */
export type SubmissionQuoteGroup = {
  latest: RfqSubmission;
  /** Older quotes for the same stream, newest first. */
  previous: RfqSubmission[];
};

function createdAtMs(sub: RfqSubmission): number {
  return sub.createdAt instanceof Date ? sub.createdAt.getTime() : 0;
}

/**
 * Group already-filtered active quote submissions into latest + history per
 * stream. `keyFor` decides which submissions supersede each other; submissions
 * mapping to the same key are one stream. Groups keep the order in which their
 * key first appears after sorting all submissions by key then date, so output
 * is stable (alphabetical by key, newest first inside each group).
 */
export function groupQuoteSubmissionHistory(
  subs: RfqSubmission[],
  keyFor: (sub: RfqSubmission) => string,
): SubmissionQuoteGroup[] {
  const byKey = new Map<string, RfqSubmission[]>();
  for (const sub of subs) {
    const key = keyFor(sub).trim().toLowerCase();
    const list = byKey.get(key) ?? [];
    list.push(sub);
    byKey.set(key, list);
  }

  const groups: SubmissionQuoteGroup[] = [];
  for (const list of Array.from(byKey.values())) {
    const sorted = list.slice().sort((a, b) => createdAtMs(b) - createdAtMs(a));
    groups.push({ latest: sorted[0]!, previous: sorted.slice(1) });
  }

  return groups.sort((a, b) =>
    keyFor(a.latest).localeCompare(keyFor(b.latest), undefined, { sensitivity: 'base' }),
  );
}

/** Submission ids in the same quote stream as {@link target} (same keyFor bucket). */
export function siblingSubmissionIdsInQuoteStream(
  target: RfqSubmission,
  subs: RfqSubmission[],
  keyFor: (sub: RfqSubmission) => string,
): string[] {
  const key = keyFor(target).trim().toLowerCase();
  return subs
    .filter(s => !!s.id && keyFor(s).trim().toLowerCase() === key)
    .map(s => s.id!);
}

/** Latest submission of every stream (what totals and approvals should count). */
export function latestQuoteSubmissions(
  subs: RfqSubmission[],
  keyFor: (sub: RfqSubmission) => string,
): RfqSubmission[] {
  return groupQuoteSubmissionHistory(subs, keyFor).map(g => g.latest);
}

/**
 * CNC quote stream key: structure set only (`cnc|full` or `cnc|id1,id2`).
 * Same structure set = one stream (revisions); different sets = concurrent streams.
 * Legacy uploads with no {@link RfqSubmission.quotedStructures} stay full-scope.
 */
export function cncSubmissionHistoryKey(
  sub?: Pick<RfqSubmission, 'quotedStructures'> | null,
): string {
  return timberQuoteStreamKeyFromParts('cnc', sub?.quotedStructures);
}
